import { ActionTree, MutationTree, GetterTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";

interface StructureState {
  loading: boolean;
  loaded: boolean;
  productTypes: any[];
}

const state: StructureState = {
  loading: false,
  loaded: false,
  productTypes: []
};

const PRODUCT_TYPES_LIST = "GET_PRODUCT_DIRECTORY_STRUCTURE_TYPES";

function treeNodeKind(recordType: string) {
  return recordType === "parent_product" ? "parent" : "type";
}

export const actions: ActionTree<StructureState, RootState> = {
  loadStructure({ commit }) {
    const vm = (this as any)._vm;
    console.debug("[ProductDirectory.Structure.loadStructure] START");

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q(PRODUCT_TYPES_LIST)
          .raw("documents.list")
          .action({
            columns: {
              id: "products_types.id",
              name: "products_types.name",
              parent_id: "products_types.parent_id",
              record_type: "products_types.record_type",
              code: "products_types.code",
              promo_name: "products_types.promo_name",
              teaser_composition: "products_types.teaser_composition",
              scope: "products_types.scope",
              composition: "products_types.composition",
              attachments: "products_types.attachments",
              label_attachments: "products_types.label_attachments",
              specification_attachments:
                "products_types.specification_attachments",
              technology_id: "products_types.technology_id",
              price_segment_id: "products_types.price_segment_id",
              trade_mark_id: "products_types.trade_mark_id",
              color: "products_types.color",
              protected_recipe: "products_types.protected_recipe",
              archived_at: "products_types.archived_at",
              technology_name: "product_technologies.name",
              price_segment_name: "product_price_segments.name",
              trade_mark_name: "parent_trade_marks.name"
            },
            from: "s:products_types",
            from_as: "products_types",
            joins: [
              {
                join: "s:product_technologies",
                type: "left",
                as: "product_technologies",
                on: {
                  $eq: [
                    "product_technologies.id",
                    "products_types.technology_id"
                  ]
                }
              },
              {
                join: "s:product_price_segments",
                type: "left",
                as: "product_price_segments",
                on: {
                  $eq: [
                    "product_price_segments.id",
                    "products_types.price_segment_id"
                  ]
                }
              },
              {
                join: "s:product_trade_marks",
                type: "left",
                as: "parent_trade_marks",
                on: {
                  $eq: [
                    "parent_trade_marks.id",
                    "products_types.trade_mark_id"
                  ]
                }
              }
            ],
            page_size: 100000,
            order: [{ $asc: ["products_types.name"] }]
          })
      ])
        .then((result: ServerResult) => {
          const content = result.getOperation(PRODUCT_TYPES_LIST).getContent();
          const documents = content.documents || [];
          const byRecordType = documents.reduce((acc: any, row: any) => {
            const key = row.record_type || "type";
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});
          console.debug("[ProductDirectory.Structure.loadStructure] SUCCESS", {
            count: documents.length,
            byRecordType
          });
          commit("SET_PRODUCT_TYPES", documents);
          commit("LOADED_SUCCESS");
          resolve(documents);
        })
        .catch((error: any) => {
          console.error("[ProductDirectory.Structure.loadStructure] ERROR", {
            error
          });
          reject(error);
        });
    });
  },

  productTypeToArchive(_ctx, productTypeId) {
    const vm = (this as any)._vm;
    console.debug("[ProductDirectory.Structure.productTypeToArchive]", {
      productTypeId
    });

    return new Promise((resolve, reject) => {
      vm.$db([
        vm.$q("PRODUCT_TYPE_TO_ARCHIVE").update(productTypeId, {
          archived_at: moment().format("YYYY-MM-DDTHH:mm:ss")
        })
      ])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("PRODUCT_TYPE_TO_ARCHIVE");
          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error(
            "[ProductDirectory.Structure.productTypeToArchive] ERROR",
            error
          );
          reject(error);
        });
    });
  },

  productTypeToUnarchive(_ctx, productTypeId) {
    const vm = (this as any)._vm;
    console.debug("[ProductDirectory.Structure.productTypeToUnarchive]", {
      productTypeId
    });

    return new Promise((resolve, reject) => {
      vm.$db([
        vm.$q("PRODUCT_TYPE_TO_UNARCHIVE").update(productTypeId, {
          archived_at: null
        })
      ])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation(
            "PRODUCT_TYPE_TO_UNARCHIVE"
          );
          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error(
            "[ProductDirectory.Structure.productTypeToUnarchive] ERROR",
            error
          );
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<StructureState> = {
  START_LOADING(state) {
    state.loading = true;
  },
  LOADED_SUCCESS(state) {
    state.loading = false;
    state.loaded = true;
  },
  SET_PRODUCT_TYPES(state, documents: any[]) {
    state.productTypes = documents || [];
  }
};

export const getters: GetterTree<StructureState, RootState> = {
  structureTree: (state: StructureState) => {
    const types = state.productTypes;
    const typeMap: any = {};
    const roots: any[] = [];

    types.forEach((type: any) => {
      typeMap[type.id] = {
        ...type,
        record_type: type.record_type || "type",
        type: treeNodeKind(type.record_type || "type"),
        children: []
      };
    });

    types.forEach((type: any) => {
      const node = typeMap[type.id];
      const recordType = type.record_type || "type";
      if (
        recordType === "parent_product" &&
        type.parent_id &&
        typeMap[type.parent_id]
      ) {
        typeMap[type.parent_id].children.push(node);
      } else if (recordType === "type") {
        roots.push(node);
      }
    });

    console.debug("[ProductDirectory.Structure.structureTree]", {
      roots: roots.length,
      parents: types.filter((t: any) => t.record_type === "parent_product")
        .length
    });

    return roots;
  }
};

export const STORE_KEY = "$_product_directory_structure";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
